import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Checkbox,
	Col,
	Divider,
	Drawer,
	Form,
	Input,
	Row,
	Space,
	Typography,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import { Moment } from 'moment';
import { PlusOutlined } from '@ant-design/icons';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import {
	DATE_AND_TIME,
	AccountingItemApprovalStates,
	FixtureCounterpartyTypes,
	HireInvoiceItemStates,
	AccountTypes,
	CargoContractSides,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { standardSort } from '@shared/utils/standardSort';
import { nowMoment } from '@shared/utils/date';
import { formatCurrency } from '@shared/utils/currency';
import { formatHumanReadable } from '@shared/utils/string';
import { sortByDates } from '@shared/utils/sortByDates';
import type { RevenueItemProps } from '@api/models/revenue-item';
import type { VoyageExpenseReceivableProps } from '@api/models/voyage-expense-receivable';
import type { VoyageExpenseProps } from '@api/models/voyage-expense';
import { VoyageRevenueItemReturnType } from '@api/utils/sequelize/getVoyageRevenueItems';
import { formatDate } from '@client/utils/formatDate';
import Card from '@client/components/Card/Card';
import EditableTable from '@client/components/EditableTable';
import StateTag from '@client/components/StateTag';
import { getFilterProps } from '@client/utils/table';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	createVoyageExpenseReceivable,
	deleteRevenueItem,
	deleteVoyageExpenseReceivable,
	getCounterparties,
	updateVoyageExpenseReceivable,
} from '@client/lib/api';
import Button from '@client/components/Button';
import EmptyText from '@client/components/EmptyText';
import CreateRevenueForm from '@client/components/CreateRevenueForm/CreateRevenueForm';
import MultiCurrencyInput from '@client/components/MultiCurrencyInput';
import Select from '@client/components/Select';
import TooltipIcon from '@client/components/TooltipIcon';
import AccountCodeOverrideFormItem from '@client/components/AccountCodeOverrideFormItem';
import CargoContractSideTag from '@client/screens/cargos/details/utils/CargoContractSideTag';
import { useVoyage } from './VoyageProvider/VoyageProvider';
import VendorExpensesCard from './VendorExpensesCard';
import EuExpenseCard from './EuExpenseCard';

const VcExpensesTab = () => {
	const {
		fixtureCurrency,
		voyageDetails,
		refreshDetails,
		cargos,
	} = useVoyage();

	const expenses = useMemo(() => {
		if (!voyageDetails?.id) {
			return [];
		}

		const sortedByDate = sortByDates(voyageDetails.voyageExpenseReceivables, 'createdAt');

		return sortedByDate.sort((a, b) => a.account.localeCompare(b.account));
	}, [voyageDetails]);

	const [charterers] = useFetchedState(() => getCounterparties(
		FixtureCounterpartyTypes.CHARTEREROWNER,
	));

	const chartererOptionsToUse = useMemo(() => {
		const hasCargoRelet = cargos?.some(
			(cargo) => cargo.contractSide === CargoContractSides.VC_IN,
		) ?? false;

		return cargos
			?.map((cargo) => {
				const charterer = charterers?.find((c) => c.id === cargo.charterer);

				if (charterer == null) {
					return null;
				}

				return {
					label: hasCargoRelet ?
						<CargoContractSideTag
							contractSide={
								cargo.contractSide === CargoContractSides.VC_IN ?
									CargoContractSides.VC_IN :
									CargoContractSides.VC_OUT
							}
							chartererName={charterer.name}
						/> :
						charterer.name,
					value: charterer.id,
				};
			})
			.filter((c): c is NonNullable<typeof c> => c != null) ?? [];
	}, [charterers, cargos]);

	const [editingId, setEditingId] = useState<number | null>(null);
	const [editingRevenueItemId, setEditingRevenueItemId] = useState<number | null>(null);
	const [drawerOpen, setDrawerOpen] = useState(false);

	const [revenueDrawerVisible, setRevenueDrawerVisible] = useState(false);

	const { revenueItems } = voyageDetails;

	const cargoCharterers = useMemo(() => {
		if (!cargos) {
			return [];
		}

		const allCharterers = cargos.map((c) => c.charterer);

		return [...new Set(allCharterers)];
	}, [cargos]);

	const required = [{ required: true, message: 'Field is required' }];

	const [form] = useForm();

	const onClose = () => {
		setEditingId(null);
		form.resetFields();
		setDrawerOpen(false);
	};

	const revenueFormOnClose = () => {
		setRevenueDrawerVisible(false);
		setEditingRevenueItemId(null);
		refreshDetails();
	};

	const saveExpense = async () => {
		const values = form.getFieldsValue();

		const {
			itemDescription,
			chartererId,
			account,
			note,
			amount,
			estimated = false,
		} = values;

		const {
			value,
			currency,
			exchangeRate,
		} = amount;

		if (editingId != null) {
			await updateVoyageExpenseReceivable(voyageDetails.id, editingId, {
				itemDescription,
				chartererId,
				account,
				amount: value,
				currency,
				exchangeRate,
				note,
				estimated,
			}, []);
		} else {
			await createVoyageExpenseReceivable({
				voyageId: voyageDetails.id,
				itemDescription,
				chartererId,
				account,
				amount: value,
				currency,
				exchangeRate,
				invoiceDate: nowMoment(),
				note,
				estimated,
			});
		}

		await refreshDetails();
		onClose();
	};

	const deleteReceivable = async (expenseId: number) => {
		await deleteVoyageExpenseReceivable(voyageDetails.id, expenseId);
		await refreshDetails();
	};

	const onDeleteRevenueItem = async (revenueItemId: number) => {
		await deleteRevenueItem(revenueItemId);
		refreshDetails();
	};

	const expenseColumns = [
		{
			title: 'Creation date',
			dataIndex: 'createdAt',
			render: (date: Moment) => formatDate(date, DATE_AND_TIME),
		},
		{
			title: 'Account',
			dataIndex: 'account',
			render: (account: string) => formatHumanReadable(account),
		},
		{
			title: 'Charterer',
			dataIndex: 'chartererId',
			render: (id: number) => charterers?.find((c) => c.id === id)?.name ?? 'N/A',
		},
		{
			title: 'Item description',
			dataIndex: 'itemDescription',
			render: (description: string) => (description === '' ? (<EmptyText />) : description),
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			align: 'right',
			render: (
				amount: number,
				record: VoyageExpenseReceivableProps,
			) => amount != null && formatCurrency(amount, record.currency),
		},
		{
			dataIndex: 'state',
			title: 'Status',
			render: (state: Values<typeof AccountingItemApprovalStates>, record: VoyageExpenseProps) => (
				<StateTag state={record.estimated ? 'estimated' : state} />
			),
			width: 100,
			sorter: standardSort('state'),
			...getFilterProps([
				HireInvoiceItemStates.PENDING,
				HireInvoiceItemStates.INVOICED,
				HireInvoiceItemStates.UNRESOLVED,
				HireInvoiceItemStates.PAID,
			], 'select', 'state', undefined, true),
		},
	];

	const revenueColumns = [
		{
			title: 'Creation date',
			dataIndex: 'createdAt',
			render: (date: Moment) => formatDate(date, DATE_AND_TIME),
		},
		{
			title: 'Subject to commissions',
			dataIndex: 'subjectToCommissions',
			render: (value: boolean) => (<span>{value ? 'Yes' : 'No'}</span>),
		},
		{
			title: 'Charterer',
			dataIndex: 'chartererId',
			render: (id: number) => charterers?.find((c) => c.id === id)?.name ?? 'N/A',
		},
		{
			title: 'Item description',
			dataIndex: 'itemDescription',
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			align: 'right' as 'right',
			render: (
				amount: number,
				record: RevenueItemProps,
			) => amount != null && formatCurrency(amount, record.currency),
		},
		{
			dataIndex: 'state',
			title: 'Status',
			render: (state: Values<typeof HireInvoiceItemStates>, record: VoyageRevenueItemReturnType) => (
				<StateTag state={record.estimated ? 'estimated' : state} />
			),
			width: 100,
			sorter: standardSort('state'),
			...getFilterProps([
				HireInvoiceItemStates.PENDING,
				HireInvoiceItemStates.INVOICED,
			], 'select', 'state', undefined, true),
		},
	];

	const editingExpenseInvoiced = useMemo(() => (
		expenses.find((e) => e.id === editingId)?.HIIVoyageExpense != null
	), [expenses, editingId]);

	const editingRevenueItem = useMemo(() => {
		return revenueItems?.find((i) => i.id === editingRevenueItemId);
	}, [revenueItems, editingRevenueItemId]);

	useEffect(() => {
		const expense = expenses.find((e) => e.id === editingId);

		if (expense != null) {
			form.setFieldsValue({
				...expense,
				amount: {
					value: expense.amount,
					currency: expense.currency,
					exchangeRate: expense.exchangeRate,
				},
				linerCost: {
					value: expense?.expense?.linerCostPricePerUnit,
					currency: expense.currency,
					exchangeRate: expense.exchangeRate,
				},
			});
		}
	}, [editingId, expenses, form]);

	return (
		<Row gutter={[16, 16]}>
			<Col span={24}>
				<Card
					title="Expenses"
					slim
					extra={(
						<Button
							onClick={() => setDrawerOpen(true)}
							type="primary"
							icon={(<PlusOutlined />)}
						>
							Create expense to/from charterer
						</Button>
					)}
				>
					<EditableTable<any, 'id'>
						dataSource={expenses}
						// @ts-ignore (align apparently makes an error? but its valid)
						columns={expenseColumns}
						onSave={() => null}
						keyDataIndex="id"
						editingRow={null}
						onDelete={deleteReceivable}
						onEditingRowChange={(_i, e) => e != null && setEditingId(e.id)}
						size={'small' as SizeType}
						pagination={false}
						enableEdit
						enableDelete
						iconButtons
						actionsTitle=""
						expandable={{
							expandedRowRender: (record) => (
								<>
									<Typography.Title level={5}>Internal note</Typography.Title>
									{record.note != null && record.note !== '' ? record.note : (
										<EmptyText />
									)}
									{record.currency !== fixtureCurrency && (
										<>
											<Divider />
											<b>Original Amount:</b>
											{` ${formatCurrency(record.amount, record.currency)}`}
											<br />
											<b>Exchange Rate:</b>
											{` ${record.exchangeRate} ${record.currency} = 1 ${fixtureCurrency} `}
										</>
									)}
								</>
							),
						}}
					/>
				</Card>
			</Col>
			<Col span={24}>
				<Card
					title="Revenues"
					slim
					extra={(
						<Button
							onClick={() => setRevenueDrawerVisible(true)}
							type="primary"
							icon={(<PlusOutlined />)}
						>
							Create revenue item
						</Button>
					)}
				>
					<EditableTable<VoyageRevenueItemReturnType, 'id'>
						pagination={false}
						dataSource={voyageDetails.revenueItems ?? []}
						columns={revenueColumns}
						onSave={() => { }}
						keyDataIndex="id"
						editingRow={null}
						iconButtons
						enableEdit
						enableDelete
						onDelete={onDeleteRevenueItem}
						onEditingRowChange={(id: number | null) => {
							setEditingRevenueItemId(id);
							setRevenueDrawerVisible(true);
						}}
						actionsTitle=""
						size={'small' as SizeType}
						expandable={{
							expandedRowRender: (record) => (
								<>
									<Typography.Title level={5}>Internal note</Typography.Title>
									{record.note != null && record.note !== '' ? record.note : (
										<EmptyText />
									)}
									{record.currency !== fixtureCurrency && (
										<>
											<Divider />
											<b>Original Amount:</b>
											{` ${formatCurrency(record.amount, record.currency)}`}
											<br />
											<b>Exchange Rate:</b>
											{` ${record.exchangeRate} ${record.currency} = 1 ${fixtureCurrency} `}
										</>
									)}
								</>
							),
						}}
					/>
				</Card>
			</Col>
			<Col span={24}>
				<VendorExpensesCard />
			</Col>
			<Col span={24}>
				<EuExpenseCard />
			</Col>
			<Drawer
				title={editingId != null ? 'Editing expense' : 'Create expense to/from charterer'}
				open={drawerOpen || editingId != null}
				onClose={onClose}
				width={500}
			>
				<Form
					form={form}
					layout="vertical"
				>
					<Form.Item
						name="account"
						label="Account"
						rules={required}
					>
						<Select
							options={[{
								label: 'Charterer',
								value: AccountTypes.CHARTERER,
							},
							{
								label: 'Owner',
								value: AccountTypes.OWNER,
							}]}
						/>
					</Form.Item>
					<Form.Item
						name="chartererId"
						label="Charterer"
						extra={editingExpenseInvoiced ?
							'Invoiced: to move it, delete this expense and create a new one' :
							undefined}
					>
						<Select
							allowClear
							disabled={editingExpenseInvoiced}
							placeholder="Select Charterer"
							options={chartererOptionsToUse}
						/>
					</Form.Item>
					<Form.Item
						name="itemDescription"
						label="Item Description"
					>
						<Input type="text" />
					</Form.Item>
					<Form.Item
						name="amount"
						label="Amount"
						rules={required}
					>
						<MultiCurrencyInput baseCurrency={fixtureCurrency} />
					</Form.Item>
					<Form.Item
						name="estimated"
						valuePropName="checked"
						initialValue={false}
						label={(
							<Space>
								Estimated
								<TooltipIcon>
									Estimated items are used for estimating P&L items,
									and cannot be invoiced or put through
									an approvals process.
									If you wish to invoice the item,
									convert the item to an actual by
									deselecting the &quot;estimated&quot; checkbox
								</TooltipIcon>
							</Space>
						)}
					>
						<Checkbox />
					</Form.Item>
					<AccountCodeOverrideFormItem />
					<Form.Item
						name="note"
						label="Internal note"
					>
						<Input.TextArea />
					</Form.Item>
				</Form>
				<Space>
					<Button
						onClick={saveExpense}
						type="primary"
					>
						Save & Close
					</Button>
					<Button>
						Close
					</Button>
				</Space>
			</Drawer>
			<CreateRevenueForm
				extraOnSave={revenueFormOnClose}
				voyageId={voyageDetails.id}
				cargos={cargos ?? []}
				editingItem={editingRevenueItem}
				open={revenueDrawerVisible}
				charterers={charterers}
				cargoCharterers={cargoCharterers}
				fixtureCurrency={fixtureCurrency}
				onClose={revenueFormOnClose}
			/>
		</Row>
	);
};

export default VcExpensesTab;
